import { Link } from "react-router-dom";
import { usePageTitle } from "@/context/ShopContext";
import Reveal from "@/components/Reveal";

const VALUES = [
  {
    title: "Femininity",
    text: "Softness is not fragility. Our shades and fabrics are chosen to honour the quiet strength of feminine grace.",
  },
  {
    title: "Modesty",
    text: "Modesty is a form of elegance — a choice made with confidence. LAMSAH exists to make that choice beautiful.",
  },
  {
    title: "Simplicity",
    text: "No excess, no noise. Only considered fabrics, honest colours, and details that reward a closer look.",
  },
];

export default function About() {
  usePageTitle("About");
  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-20 md:pt-28 pb-16">
        <Reveal>
          <p className="overline-label mb-4">Our Story</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight max-w-2xl leading-[1.1]">
            The Story Behind LAMSAH
          </h1>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center pb-24">
        <Reveal className="md:col-span-6">
          <img src="/images/about.jpg" alt="Soft folds of warm beige fabric" className="w-full aspect-[6/5] object-cover" loading="lazy" />
        </Reveal>
        <Reveal delay={0.15} className="md:col-span-6">
          <p className="font-arabic text-4xl text-rose" lang="ar" dir="rtl">لمسة</p>
          <h2 className="font-serif text-2xl sm:text-3xl font-light mt-4">A touch — that is all it takes.</h2>
          <div className="text-sm sm:text-base text-espresso/75 leading-relaxed mt-6 space-y-5">
            <p>
              In Arabic, <span className="font-arabic" lang="ar" dir="rtl">لمسة</span> — lamsah — means
              “a touch.” The lightest contact of silk against skin. The finishing detail
              that completes an outfit. The small, almost invisible thing that changes
              how a woman feels when she catches her reflection.
            </p>
            <p>
              LAMSAH was born from a simple belief: that modest fashion deserves the same
              artistry, the same quality, and the same quiet luxury as any couture house.
              Inspired by the warmth of Saudi elegance — sun-washed neutrals, desert
              roses, the calm of early morning light — each hijab in our collection is
              chosen by hand for its drape, its texture, and its honesty.
            </p>
            <p>
              We are a young boutique, and we prefer it that way. Small enough to care
              about every fold, every shade, every parcel that leaves our hands.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl font-light tracking-tight">What We Hold Dear</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14 mt-12">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="border-t border-espresso/20 pt-6">
                  <p className="font-serif text-lg text-rose">0{i + 1}</p>
                  <h3 className="font-serif text-xl mt-2">{v.title}</h3>
                  <p className="text-sm text-espresso/70 leading-relaxed mt-3">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link to="/shop" data-testid="about-shop-button" className="btn-primary mt-14">Explore the Collection</Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
